import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Moon, Sun, UserCog, LogOut, MapPin, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useAuth } from '../contexts/AuthContext';
import { checkCredits } from '../services/creditsService';

interface BurgerMenuProps {
  userName?: string;
  credits?: number;
  showUserInfo?: boolean;
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ userName: propUserName, credits: propCredits, showUserInfo = false }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isDarkMode, toggleTheme } = useTheme();
  const { distanceUnit, toggleDistanceUnit } = useDistanceUnit();
  const { user, profile, signOut } = useAuth();
  
  // Internal state for user data (fetched from context/services)
  const [internalCredits, setInternalCredits] = useState<number>(0);
  const [avatarError, setAvatarError] = useState(false);
  
  // Use internal data if props are not provided
  const userName = propUserName || profile?.full_name || 'User';
  const credits = propCredits !== undefined ? propCredits : internalCredits;
  
  // Fetch credits when user is available
  useEffect(() => {
    const fetchCredits = async () => {
      if (user && propCredits === undefined) {
        try {
          const creditsData = await checkCredits(user.id);
          setInternalCredits(creditsData.total_credits);
        } catch (error) {
          console.error('Error fetching credits in BurgerMenu:', error);
          setInternalCredits(0);
        }
      }
    };
    
    fetchCredits();
  }, [user, propCredits]);

  // Reset avatar error when profile changes
  useEffect(() => {
    setAvatarError(false);
  }, [profile?.avatar_url]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    try {
      await signOut();
      // Wait a moment for auth state to fully clear
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch (error) {
      console.error('Error signing out:', error);
      // Still navigate to login page even if signOut fails
      setTimeout(() => {
        navigate('/');
      }, 100);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <motion.div 
        onClick={toggleDropdown}
        whileTap={{
          backgroundColor: isDropdownOpen ? 'var(--primary-color)' : 'rgba(79, 70, 229, 0.15)',
          transition: { duration: 0 }
        }}
        animate={{
          backgroundColor: isDropdownOpen ? 'var(--primary-color)' : 'var(--secondary-color)',
          transition: { duration: 0 }
        }}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {isDropdownOpen ? (
          <X size={20} color="white" />
        ) : (
          <Menu size={20} color="var(--text-primary)" />
        )}
      </motion.div>
      
      {/* Dropdown Menu */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.92 }}
            transition={{ 
              type: 'spring',
              stiffness: 500,
              damping: 35,
              mass: 0.8
            }}
            style={{
              position: 'absolute',
              top: '45px',
              right: '0',
              minWidth: '240px',
              backgroundColor: 'var(--card-background)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              overflow: 'hidden',
              transformOrigin: 'top right',
              willChange: 'opacity, transform'
            }}
          >
          <div style={{ padding: '6px 0' }}>
            {/* User Info Section - Always show when user is logged in */}
            {user && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05, duration: 0.25, ease: 'easeOut' }}
                style={{
                  padding: '4px 16px 10px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                {/* Profile Picture */}
                <div 
                  onClick={() => {
                    setIsDropdownOpen(false);
                    navigate('/account');
                  }}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '2px solid var(--border-color)',
                    backgroundColor: 'var(--secondary-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    cursor: 'pointer'
                  }}>
                  {profile?.avatar_url && !avatarError ? (
                    <img
                      src={profile.avatar_url}
                      alt={userName}
                      onError={() => setAvatarError(true)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <img
                      src={`${process.env.PUBLIC_URL}/PFP.svg`}
                      alt={userName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                </div>
                
                {/* Name and Credits */}
                <div style={{ flex: 1 }}>
                  <div 
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/account');
                    }}
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}>
                    {userName}
                  </div>
                  <div 
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/buy-credits');
                    }}
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer'
                    }}>
                    {credits} {credits === 1 ? 'credit' : 'credits'}
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Login Menu Item - Only show when user is NOT logged in */}
            {!user && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.25, ease: 'easeOut' }}
                style={{
                  padding: '14px 16px',
                  margin: '-4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--primary-color)',
                  borderBottom: '1px solid var(--border-color)',
                  marginBottom: '0px',
                  paddingBottom: '18px'
                }}
                className="dropdown-item"
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/');
                }}
              >
                <UserCog size={16} />
                Login / Signup
              </motion.div>
            )}
            
            {/* Theme Toggle Menu Item */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: user ? 0.1 : 0.15, duration: 0.25, ease: 'easeOut' }}
              style={{
                padding: '14px 16px',
                margin: '-4px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)'
              }}
              className="dropdown-item"
              onClick={() => {
                toggleTheme();
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {isDarkMode ? <Moon size={16} /> : <Sun size={16} />}
                {isDarkMode ? 'Dark Mode' : 'Light Mode'}
              </div>
              <div style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: isDarkMode ? 'var(--primary-color)' : 'var(--border-color)',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  position: 'absolute',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  top: '3px',
                  left: isDarkMode ? '23px' : '3px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }} />
              </div>
            </motion.div>

            {/* Distance Unit Segmented Control Menu Item */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: user ? 0.15 : 0.2, duration: 0.25, ease: 'easeOut' }}
              style={{
                padding: '14px 16px',
                margin: '-4px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MapPin size={16} />
                Distance
              </div>
              <div style={{
                position: 'relative',
                display: 'flex',
                backgroundColor: 'var(--secondary-color)',
                borderRadius: '14px',
                padding: '3px',
                border: '1px solid var(--border-color)'
              }}>
                {/* Animated sliding background */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: '3px',
                    bottom: '3px',
                    width: 'calc(50% - 3px)',
                    backgroundColor: 'var(--primary-color)',
                    borderRadius: '11px',
                    zIndex: 0
                  }}
                  initial={false}
                  animate={{
                    left: distanceUnit === 'mi' ? '3px' : 'calc(50% + 0px)'
                  }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
                <div
                  onClick={() => distanceUnit !== 'mi' && toggleDistanceUnit()}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '11px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: distanceUnit === 'mi' ? 'default' : 'pointer',
                    backgroundColor: 'transparent',
                    color: distanceUnit === 'mi' ? 'white' : 'var(--text-secondary)',
                    transition: 'color 0.2s ease',
                    userSelect: 'none',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  mi
                </div>
                <div
                  onClick={() => distanceUnit !== 'km' && toggleDistanceUnit()}
                  style={{
                    padding: '5px 14px',
                    borderRadius: '11px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: distanceUnit === 'km' ? 'default' : 'pointer',
                    backgroundColor: 'transparent',
                    color: distanceUnit === 'km' ? 'white' : 'var(--text-secondary)',
                    transition: 'color 0.2s ease',
                    userSelect: 'none',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  km
                </div>
              </div>
            </motion.div>
            
            {/* My Account Menu Item - Only show when logged in, in its own section */}
            {user && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.25, ease: 'easeOut' }}
                style={{
                  padding: '16px 16px 16px 16px',
                  margin: '0 0 -4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginTop: '4px',
                  borderTop: '1px solid var(--border-color)'
                }}
                className="dropdown-item"
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/account');
                }}
              >
                <UserCog size={16} />
                My Account
              </motion.div>
            )}
            
            {/* How It Works Menu Item - Always show */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: user ? 0.25 : 0.25, duration: 0.25, ease: 'easeOut' }}
              style={{
                padding: '16px 16px 16px 16px',
                margin: '0 0 -4px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginTop: '4px',
                borderTop: '1px solid var(--border-color)'
              }}
              className="dropdown-item"
              onClick={() => {
                setIsDropdownOpen(false);
                navigate('/faq');
              }}
            >
              <HelpCircle size={16} />
              How It Works
            </motion.div>
            
            {/* Logout Menu Item - Only show when logged in */}
            {user && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.25, ease: 'easeOut' }}
                style={{
                  padding: '16px 16px 16px 16px',
                  margin: '0 0 -4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--error-color)',
                  marginTop: '4px',
                  borderTop: '1px solid var(--border-color)'
                }}
                className="dropdown-item-danger"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                Logout
              </motion.div>
            )}
          </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BurgerMenu;

